<template>
  <v-container class="guided-recording-organizer pa-4 pa-sm-6" data-cy="guidedRecording-organizer">
    <v-row justify="center">
      <v-col>
        <header class="d-flex flex-wrap align-center justify-space-between ga-3 mb-5">
          <div v-if="!embedded">
            <h1 class="text-h5">{{ $t('routes.guidedRecording.pageTitle') }}</h1>
            <p class="text-body-2 mb-0">{{ $t('routes.guidedRecording.pageDescription') }}</p>
          </div>
          <v-chip :color="statusColor" variant="flat" data-cy="guidedRecording-organizer-status">
            <v-icon start aria-hidden="true">{{ statusIcon }}</v-icon>
            {{ statusLabel }}
          </v-chip>
        </header>

        <v-card color="surface" class="pa-4 pa-sm-6">
          <GuidedRecordingForm
            ref="organizerForm"
            v-model="form"
            :questions="questions"
            :field-errors="fieldErrors"
            :form-error="formError"
            :destination-error="destinationError"
            :question-error="questionError"
            :announcement="announcement"
            :disabled="isSubmitting || isHydratingInvitation || invitationHydrationFailed || !canOrganize"
            :saving="isSaving"
            :mutating-questions="isMutatingQuestions"
            @save="save"
            @clear-field-error="clearFieldError"
            @add-question="addQuestion"
            @update-question="updateQuestion"
            @delete-question="deleteQuestion"
            @reorder-questions="reorderQuestions"
          />
          <section v-if="publicUrl" class="mt-5" aria-labelledby="guided-recording-public-url-heading">
            <h2 id="guided-recording-public-url-heading" class="text-subtitle-1 mb-2">{{ $t('routes.guidedRecording.publicUrl') }}</h2>
            <v-text-field :model-value="publicUrl" readonly :label="$t('routes.guidedRecording.publicUrl')" data-cy="guidedRecording-organizer-publicUrl">
              <template v-slot:append-inner>
                <v-btn
                  :disabled="!canAccessClipboard"
                  :aria-label="$t('routes.guidedRecording.copyPublicUrl')"
                  size="small"
                  density="comfortable"
                  variant="text"
                  width="36"
                  height="36"
                  rounded="36"
                  icon
                  tile
                  data-cy="guidedRecording-organizer-copyPublicUrl"
                  @click="copyPublicUrl"
                >
                  <v-icon>mdi-content-copy</v-icon>
                </v-btn>
              </template>
            </v-text-field>
          </section>
        </v-card>

        <v-alert v-if="!canOrganize" type="warning" variant="tonal" class="mt-5" role="alert">
          {{ $t('routes.guidedRecording.permissionRequired') }}
        </v-alert>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import Api from '@/api/Api.js';
import {
  toGuidedRecordingConsentConfiguration,
  toGuidedRecordingExpiryConfiguration,
} from '@/api/modules/GuidedRecordingCalls.js';
import GuidedRecordingForm from '@/routes/guided-recording/components/GuidedRecordingForm.vue';
import { useTenantStore } from '@/stores/TenantStore.js';
import { addDays } from 'date-fns/addDays';
import { UTCDate } from '@date-fns/utc';
import { formatDate } from '@/components/formatters.js';
import { useNodeStore } from '@/stores/NodeStore.js';

const EMPTY_FORM = () => ({
  destinationNodeId: '',
  destinationName: '',
  title: '',
  introduction: '',
  instructions: '',
  active: true,
  questionText: '',
  questionRequired: true,
  questionNote: '',
  questionScript: '',
  questionMaximumDurationSeconds: null,
  consentRequired: false,
  audioAnalysisEnabled: false,
  expiresAt: null,
});

const toMaximumDurationSeconds = (question) => (
  question.maximumDurationSeconds ?? question.maximumdurationseconds ?? null
);

const toQuestions = (questions = []) => questions
  .map((question) => ({
    id: question.id,
    order: question.order,
    text: question.text,
    required: question.required !== false,
    note: question.note ?? '',
    script: question.script ?? '',
    maximumDurationSeconds: toMaximumDurationSeconds(question),
  }))
  .sort((first, second) => first.order - second.order);

const toUpdatedAt = (record) => record?.updatedAt || record?.updatedat || '';

const toInvitationId = (invitation) => (
  typeof invitation?.id === 'string' && invitation.id ? invitation.id : ''
);

const toProjectNodeId = (invitation) => (
  invitation?.projectNodeId || invitation?.projectnode?.id || ''
);

const toDestinationNodeId = (invitation) => (
  invitation?.destinationNodeId || invitation?.destinationnodeid || invitation?.destinationnode?.id || ''
);

const toInvitationExpiry = (invitation) => invitation?.expiresAt ?? invitation?.expiresat ?? null;
const toDateOnly = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      return null;
    }

    return [
      value.getFullYear(),
      String(value.getMonth() + 1).padStart(2, '0'),
      String(value.getDate()).padStart(2, '0'),
    ].join('-');
  }

  return typeof value === 'string' ? value.slice(0, 10) : null;
};


const toInvitationForm = (invitation) => ({
  ...EMPTY_FORM(),
  destinationNodeId: toDestinationNodeId(invitation),
  destinationName: invitation?.destinationName || invitation?.destinationnode?.name || '',
  title: invitation?.title || '',
  introduction: invitation?.introduction || '',
  instructions: invitation?.instructions || '',
  active: invitation?.active === true,
  consentRequired: invitation?.consentRequired ?? invitation?.consentrequired ?? invitation?.consent?.required ?? false,
  audioAnalysisEnabled: invitation?.audioAnalysisEnabled ?? invitation?.audioanalysisenabled ?? false,
  expiresAt: toDateOnly(toInvitationExpiry(invitation)),
});

const toInvitationQuestions = (invitation) => toQuestions(
  Array.isArray(invitation?.questions) ? invitation.questions : [],
);

const reorderById = (questions, questionId, direction) => {
  const fromIndex = questions.findIndex((question) => question.id === questionId);
  const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
  if (fromIndex < 0 || toIndex < 0 || toIndex >= questions.length) {
    return questions;
  }

  const reordered = [...questions];
  [reordered[fromIndex], reordered[toIndex]] = [reordered[toIndex], reordered[fromIndex]];
  return reordered.map((question, index) => ({ ...question, order: index + 1 }));
};

export default {
  name: 'GuidedRecordingOrganizer',
  components: { GuidedRecordingForm },
  props: {
    projectNodeId: { type: String, default: '' },
    selectedInvitation: { type: Object, default: null },
    embedded: { type: Boolean, default: false },
  },
  data() {
    return {
      form: EMPTY_FORM(),
      fieldErrors: {},
      formError: '',
      destinationError: '',
      questionError: '',
      announcement: '',
      invitation: null,
      invitationUpdatedAt: '',
      questions: [],
      publicCode: '',
      isHydratingInvitation: false,
      invitationHydrationFailed: false,
      isSaving: false,
      isMutatingQuestions: false,
      questionMutationQueue: Promise.resolve(),
    };
  },
  computed: {
    activeProjectNodeId() {
      return this.projectNodeId || toProjectNodeId(this.selectedInvitation) || this.$route.params.id;
    },
    selectedInvitationId() {
      return toInvitationId(this.selectedInvitation);
    },
    selectedInvitationScope() {
      return this.projectNodeId || toProjectNodeId(this.selectedInvitation);
    },
    selectedInvitationKey() {
      return `${this.selectedInvitationId}:${this.selectedInvitationScope}`;
    },
    savedActive() {
      return this.invitation ? this.invitation.active === true : true;
    },
    statusColor() {
      return this.savedActive ? 'success' : 'secondary';
    },
    statusIcon() {
      return this.savedActive ? 'mdi-check-circle-outline' : 'mdi-close-circle-outline';
    },
    statusLabel() {
      return this.$t(`routes.guidedRecording.${this.savedActive ? 'active' : 'inactive'}`);
    },
    isSubmitting() {
      return this.isSaving || this.isMutatingQuestions;
    },
    canOrganize() {
      return this.$canControlShareLinks()
        && this.$canControlNodeAndPod()
        && this.$canUploadFileAndAsset();
    },
    publicUrl() {
      return this.publicCode
        ? `${window.location.origin}/guided-recording/${this.publicCode}`
        : '';
    },
    canAccessClipboard() {
      return window.isSecureContext && Boolean(navigator.clipboard?.writeText);
    },
  },
  watch: {
    selectedInvitationKey: {
      immediate: true,
      handler() {
        this.hydrateSelectedInvitation();
      },
    },
    async 'form.destinationNodeId'(destinationNodeId) {
      if (!destinationNodeId) {
        return;
      }

      this.destinationError = '';
      try {
        const node = await useNodeStore().getNodeInfo(destinationNodeId);
        if (this.form.destinationNodeId === destinationNodeId) {
          this.form = { ...this.form, destinationName: node?.name || '' };
        }
      } catch {
        if (this.form.destinationNodeId === destinationNodeId) {
          this.form = { ...this.form, destinationName: '' };
        }
      }
    },
  },
  methods: {
    async save(initialValues = {}) {
      if (!this.canOrganize || this.isSaving || this.isMutatingQuestions || this.isHydratingInvitation
        || this.invitationHydrationFailed) {
        return false;
      }

      this.clearErrors();
      this.isSaving = true;
      try {
        if (this.invitation) {
          await this.updateInvitation();
        } else {
          await this.createInvitation(initialValues.questions);
        }
        this.$showMessage(this.$t('routes.guidedRecording.saved'));
        return true;
      } catch (error) {
        this.handleSaveError(error);
        return false;
      } finally {
        this.isSaving = false;
      }
    },
    async createInvitation(questions) {
      const result = await Api.guidedRecording.createInvitation(
        this.activeProjectNodeId,
        this.toCreatePayload(questions),
      );
      this.applyInvitation(result);
    },
    async updateInvitation() {
      const result = await Api.guidedRecording.updateInvitation(
        this.activeProjectNodeId,
        this.invitation.id,
        this.toUpdatePayload(),
      );
      this.applyInvitation(result);
    },
    async addQuestion(question) {
      if (!this.canOrganize || this.isSubmitting || this.isHydratingInvitation) {
        return;
      }

      if (!this.invitation) {
        return;
      }

      await this.runQuestionMutation(async () => {
        const result = await Api.guidedRecording.createQuestion(
          this.activeProjectNodeId,
          this.invitation.id,
          this.toQuestionPayload(question),
        );
        this.questions = toQuestions([...this.questions, result.question]);
        this.invitationUpdatedAt = toUpdatedAt(result);
        this.announcement = this.$t('routes.guidedRecording.questionAdded', {
          position: result.question.order,
        });
      });
    },
    async updateQuestion({ questionId, changes }) {
      if (!this.canMutateQuestion(questionId)) {
        return;
      }

      const previousQuestions = this.questions;
      this.questions = this.questions.map((question) => (
        question.id === questionId ? { ...question, ...this.toQuestionPayload(changes) } : question
      ));
      await this.runQuestionMutation(async () => {
        const result = await Api.guidedRecording.updateQuestion(
          this.activeProjectNodeId,
          this.invitation.id,
          questionId,
          {
            ...this.toQuestionPayload(changes),
            expectedInvitationUpdatedAt: this.invitationUpdatedAt,
          },
        );
        this.invitationUpdatedAt = toUpdatedAt(result);
        this.announcement = this.$t('routes.guidedRecording.questionSaved');
      }, previousQuestions);
    },
    async deleteQuestion(questionId) {
      if (!this.canMutateQuestion(questionId) || this.questions.length <= 1) {
        return;
      }

      const previousQuestions = this.questions;
      this.questions = this.questions.filter((question) => question.id !== questionId)
        .map((question, index) => ({ ...question, order: index + 1 }));
      await this.runQuestionMutation(async () => {
        const result = await Api.guidedRecording.deleteQuestion(
          this.activeProjectNodeId,
          this.invitation.id,
          questionId,
          this.invitationUpdatedAt,
        );
        this.invitationUpdatedAt = toUpdatedAt(result);
        this.announcement = this.$t('routes.guidedRecording.questionDeleted');
      }, previousQuestions);
    },
    async reorderQuestions({ questionId, direction }) {
      if (!this.canMutateQuestion(questionId)) {
        return;
      }

      const previousQuestions = this.questions;
      const reorderedQuestions = reorderById(this.questions, questionId, direction);
      if (reorderedQuestions === this.questions) {
        return;
      }

      this.questions = reorderedQuestions;
      await this.runQuestionMutation(async () => {
        const result = await Api.guidedRecording.reorderQuestions(
          this.activeProjectNodeId,
          this.invitation.id,
          this.questions.map((question) => question.id),
          this.invitationUpdatedAt,
        );
        this.invitationUpdatedAt = toUpdatedAt(result);
        const position = this.questions.findIndex((question) => question.id === questionId) + 1;
        this.announcement = this.$t('routes.guidedRecording.questionMoved', { position });
      }, previousQuestions);
    },
    canMutateQuestion(questionId) {
      return Boolean(
        this.invitation
        && this.invitationUpdatedAt
        && questionId
        && !this.isMutatingQuestions
        && !this.isHydratingInvitation && !this.isSaving && this.canOrganize,
      );
    },
    async runQuestionMutation(mutation, rollbackQuestions = null) {
      this.questionError = '';
      this.isMutatingQuestions = true;
      const queuedMutation = this.questionMutationQueue.then(mutation);
      this.questionMutationQueue = queuedMutation.catch(() => undefined);
      try {
        await queuedMutation;
      } catch {
        if (rollbackQuestions) {
          this.questions = rollbackQuestions;
        }
        this.questionError = this.$t('routes.guidedRecording.questionMutationFailed');
      } finally {
        this.isMutatingQuestions = false;
      }
    },
    toQuestionPayload(question) {
      return {
        text: question.text.trim(),
        required: question.required !== false,
        note: question.note || null,
        script: question.script || null,
        maximumDurationSeconds: question.maximumDurationSeconds ?? null,
      };
    },
    toCreatePayload(questions = null) {
      const initialQuestions = Array.isArray(questions) ? questions : [{
        text: this.form.questionText,
        required: this.form.questionRequired,
        note: this.form.questionNote,
        script: this.form.questionScript,
        maximumDurationSeconds: this.form.questionMaximumDurationSeconds,
      }];

      return {
        ...this.toUpdatePayload(),
        questions: initialQuestions.map((question) => this.toQuestionPayload(question)),
      };
    },
    toUpdatePayload() {
      return {
        destinationNodeId: this.form.destinationNodeId,
        title: this.form.title.trim(),
        introduction: this.form.introduction,
        instructions: this.form.instructions,
        active: this.form.active === true,
        ...toGuidedRecordingConsentConfiguration(this.form.consentRequired),
        audioAnalysisEnabled: this.form.audioAnalysisEnabled === true,
        ...toGuidedRecordingExpiryConfiguration(this.form.expiresAt, Boolean(this.invitation)),
      };
    },
    clearErrors() {
      this.fieldErrors = {};
      this.formError = '';
      this.destinationError = '';
      this.questionError = '';
    },
    clearFieldError(field) {
      this.fieldErrors = Object.fromEntries(
        Object.entries(this.fieldErrors).filter(([errorField]) => errorField !== field),
      );
    },
    hasExpiryValidationError(error) {
      return Boolean(this.form.expiresAt && error?.response?.status === 406);
    },
    handleSaveError(error) {
      const status = error?.response?.status;
      if (status === 403 || status === 404) {
        this.destinationError = this.$t('routes.guidedRecording.destinationUnavailable');
        return;
      }

      if (this.hasExpiryValidationError(error)) {
        this.fieldErrors = {
          ...this.fieldErrors,
          expiresAt: this.$t('routes.guidedRecording.expiryInvalid'),
        };
        return;
      }

      this.formError = this.$localizeMessage(
        error?.response?.data?.error,
        this.$t('routes.guidedRecording.safeError'),
      );
    },
    async copyPublicUrl() {
      try {
        await navigator.clipboard.writeText(this.publicUrl);
        this.$showMessage(this.$t('routes.guidedRecording.copiedPublicUrl'));
      } catch {
        this.$showMessage(this.$t('routes.guidedRecording.copyFailed'), { type: 'error' });
      }
    },
    resetInvitation(initializeNew = true) {
      this.form = initializeNew ? this.newForm() : EMPTY_FORM();
      this.invitation = null;
      this.invitationUpdatedAt = '';
      this.questions = [];
      this.publicCode = '';
      this.announcement = '';
      this.clearErrors();
    },
    newForm() {
      return {
        ...EMPTY_FORM(),
        expiresAt: formatDate(addDays(new UTCDate(), useTenantStore().getTenantLinksExpiryDays)),
      };
    },
    applyInvitation(invitation) {
      this.form = toInvitationForm(invitation);
      this.invitation = invitation;
      this.invitationUpdatedAt = toUpdatedAt(invitation);
      this.questions = toInvitationQuestions(invitation);
      this.publicCode = typeof invitation?.code === 'string' ? invitation.code : '';
      this.clearErrors();
    },
    async hydrateSelectedInvitation() {
      const invitationId = this.selectedInvitationId;
      const projectNodeId = this.selectedInvitationScope;
      if (!invitationId) {
        this.isHydratingInvitation = false;
        this.invitationHydrationFailed = false;
        this.resetInvitation();
        return;
      }

      this.resetInvitation(false);
      this.isHydratingInvitation = true;
      this.invitationHydrationFailed = false;
      if (!projectNodeId) {
        this.isHydratingInvitation = false;
        this.invitationHydrationFailed = true;
        this.formError = this.$t('routes.guidedRecording.safeError');
        return;
      }


      try {
        const invitation = await Api.guidedRecording.getInvitation(projectNodeId, invitationId);
        if (this.selectedInvitationKey === `${invitationId}:${projectNodeId}`) {
          this.applyInvitation(invitation);
        }
      } catch (error) {
        if (this.selectedInvitationKey === `${invitationId}:${projectNodeId}`) {
          this.invitationHydrationFailed = true;
          this.formError = this.$localizeMessage(
            error?.response?.data?.error,
            this.$t('routes.guidedRecording.safeError'),
          );
        }
      } finally {
        if (this.selectedInvitationKey === `${invitationId}:${projectNodeId}`) {
          this.isHydratingInvitation = false;
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@layer app {
  .guided-recording-organizer {
    max-width: 1200px;
  }
}
</style>
